import { useState, useMemo } from 'react';
import {
  BadgeCheck,
  FileText,
  KeyRound,
  LogOut,
  Pencil,
  RotateCcw,
  Shield,
  Trash2,
  UserCheck,
  UserX,
  Users,
} from 'lucide-react';
import { useRoles } from '@/features/roles/hooks/useRoles';
import { usePermissions } from '@/hooks/usePermissions';
import type { User, UserListItem } from '@/types';
import { Badge, Button, ConfirmDialog, Modal } from '@/components/ui';
import type { BadgeVariant } from '@/components/ui';
import { Avatar } from '@/components/common/Avatar';
import { TableRowAction, TableRowActions } from '@/components/common/TableRowActions';
import {
  TableRowActionsMenu,
  TableRowActionsMenuDivider,
  TableRowActionsMenuItem,
} from '@/components/common/TableRowActionsMenu';
import { getCompanyColor } from '@/features/shifts/utils/companyColor';
import { useAuthStore } from '@/stores/useAuthStore';
import { cn } from '@/lib/cn';
import {
  DataTable,
  DataTableCard,
  DataTableHead,
  DataTableHeadCell,
  DataTableBody,
  DataTableRow,
  DataTableCell,
} from '@/components/common/DataTable';
import { TableSkeleton } from '@/components/common/TableSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { QueryErrorState } from '@/components/common/QueryErrorState';
import { UserProfilePanel, type ProfileMode } from './profile/UserProfilePanel';
import { ChangeUserRoleModal } from './ChangeUserRoleModal';
import { ChangeUserStatusModal } from './ChangeUserStatusModal';
import { UserDocumentsSection } from './UserDocumentsSection';
import { TerminateUserModal } from './TerminateUserModal';
import {
  useConfirmUserPermanent,
  useDeleteUser,
  useForceLogoutUser,
  useRecoverUser,
  useTriggerUserPasswordReset,
} from '../hooks/useUsers';
import { useDocumentCountSummary } from '../hooks/useUserDocuments';
import { adminPasswordResetConfirmMessage } from '../utils/adminPasswordReset';

export interface UserListProps {
  users: UserListItem[];
  isLoading: boolean;
  error: Error | null;
  /** Re-runs the users query from the error state's retry button. */
  onRetry?: () => Promise<unknown> | void;
  /** Switches the empty state from "none yet" to "nothing matches". */
  hasActiveFilters?: boolean;
  onResetFilters?: () => void;
}

const STATUS_VARIANT: Partial<Record<UserListItem['status'], BadgeVariant>> = {
  active: 'success',
  inactive: 'neutral',
  deleted: 'danger',
};

function getLifecycleStatus(user: UserListItem): string | undefined {
  const employment = user.employment as Record<string, unknown> | undefined;
  const explicit = employment?.employmentStatus;
  if (typeof explicit === 'string') return explicit;
  if (user.status === 'exited') return 'terminated';
  if (typeof employment?.confirmationDate === 'string') return 'permanent';
  if (typeof employment?.probationEndDate === 'string') return 'probation';
  return undefined;
}

export function UserList({
  users,
  isLoading,
  error,
  onRetry,
  hasActiveFilters = false,
  onResetFilters,
}: UserListProps): React.ReactElement {
  const { can } = usePermissions();
  const { data: roles = [] } = useRoles({ enabled: can('roles', 'read') });
  const roleNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of roles) m.set(r._id, r.name);
    return m;
  }, [roles]);

  const [profile, setProfile] = useState<{ userId: string; mode: ProfileMode } | null>(null);
  const [statusUserId, setStatusUserId] = useState<string | null>(null);
  const [roleUserId, setRoleUserId] = useState<string | null>(null);
  const [documentsUserId, setDocumentsUserId] = useState<string | null>(null);
  const [openMenuRowId, setOpenMenuRowId] = useState<string | null>(null);
  const [userToTerminate, setUserToTerminate] = useState<UserListItem | null>(null);
  const [userToResetPassword, setUserToResetPassword] = useState<Pick<
    User,
    '_id' | 'email' | 'firstName' | 'lastName'
  > | null>(null);
  const [userToDelete, setUserToDelete] = useState<(Pick<User, '_id'> & { name: string }) | null>(
    null
  );
  const [userToRecover, setUserToRecover] = useState<(Pick<User, '_id'> & { name: string }) | null>(
    null
  );

  const canUpdateUser = can('users', 'update');
  const canManageRoles = can('users', 'manage_roles');
  const canDeleteUser = can('users', 'delete');

  const deleteUser = useDeleteUser();
  const recoverUser = useRecoverUser();
  const confirmPermanent = useConfirmUserPermanent();
  const triggerPasswordReset = useTriggerUserPasswordReset();
  const forceLogout = useForceLogoutUser();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const [userToForceLogout, setUserToForceLogout] = useState<Pick<
    UserListItem,
    '_id' | 'email' | 'firstName' | 'lastName'
  > | null>(null);
  const canManageDocuments =
    can('documents', 'read') ||
    can('documents', 'create') ||
    can('documents', 'delete') ||
    can('users', 'update');
  const { data: documentSummary = [] } = useDocumentCountSummary({
    enabled: canManageDocuments,
  });
  const docCountByUserId = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of documentSummary) m.set(s.userId, s.documentCount);
    return m;
  }, [documentSummary]);

  const handleDeleteClick = (user: Pick<User, '_id'> & { name: string }): void => {
    setUserToDelete(user);
  };

  const handleConfirmDelete = (): void => {
    if (userToDelete) {
      deleteUser.mutate(userToDelete._id);
      if (profile?.userId === userToDelete._id) setProfile(null);
      setUserToDelete(null);
    }
  };

  const handleRecoverClick = (user: Pick<User, '_id'> & { name: string }): void => {
    setUserToRecover(user);
  };

  const handleConfirmRecover = (): void => {
    if (userToRecover) {
      recoverUser.mutate(userToRecover._id);
      setUserToRecover(null);
    }
  };

  const handleConfirmResetPassword = (): void => {
    if (!userToResetPassword) return;
    triggerPasswordReset.mutate(
      { id: userToResetPassword._id },
      {
        onSuccess: () => {
          setUserToResetPassword(null);
        },
      }
    );
  };

  const showActionsColumn = canUpdateUser || canManageRoles || canDeleteUser || canManageDocuments;
  const columnCount = 5 + (canManageDocuments ? 1 : 0) + (showActionsColumn ? 1 : 0);

  if (isLoading) {
    return (
      <DataTableCard>
        <TableSkeleton rows={5} cols={columnCount} />
      </DataTableCard>
    );
  }

  if (error) {
    return (
      <QueryErrorState
        error={error}
        title="Couldn't load users"
        onRetry={onRetry}
      />
    );
  }

  if (users.length === 0) {
    return (
      <EmptyState
        icon={Users}
        title={hasActiveFilters ? 'No users match these filters' : 'No users yet'}
        description={
          hasActiveFilters
            ? 'Try a different company, role, status, or search term.'
            : 'Create the first user to start building your team.'
        }
        action={
          hasActiveFilters && onResetFilters ? (
            <Button variant="secondary" size="sm" onClick={onResetFilters}>
              Reset filters
            </Button>
          ) : null
        }
      />
    );
  }

  /**
   * Everything a row shows, derived once so the table and the mobile card list
   * can never drift apart.
   */
  const describeUser = (user: UserListItem) => {
    const fullName = [user.firstName, user.lastName]
      .filter((part) => part && part.trim())
      .join(' ');
    const displayName = fullName || user.name?.trim() || user.email;
    const email = user.email?.trim();
    const companies =
      user.companies && user.companies.length > 0
        ? user.companies
        : user.company
          ? [user.company]
          : [];
    return {
      displayName,
      // Secondary line only when it adds something the name doesn't already say.
      email: email && email !== displayName ? email : null,
      companies,
      roleName: user.roleName ?? roleNameById.get(user.roleId) ?? '—',
      isDeleted: Boolean(user.isDeleted || user.status === 'deleted'),
      lifecycleStatus: getLifecycleStatus(user),
      documentCount: docCountByUserId.get(user._id) ?? 0,
    };
  };
  type UserRow = ReturnType<typeof describeUser>;

  /** Pointer convenience; the name button carries the same behaviour for keyboards. */
  const handleRowActivate = (user: UserListItem, row: UserRow): void => {
    if (row.isDeleted) return;
    if (window.getSelection()?.toString()) return;
    setProfile({ userId: user._id, mode: 'view' });
  };

  const renderPerson = (user: UserListItem, row: UserRow, size: 'md' | 'lg'): React.ReactElement => (
    <div className="flex min-w-0 items-center gap-3">
      <Avatar name={row.displayName} size={size} tone="soft" />
      <div className="min-w-0">
        <button
          type="button"
          className="block max-w-full cursor-pointer truncate rounded text-left font-medium text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-default dark:text-gray-100"
          aria-label={`View details for ${row.displayName}`}
          disabled={row.isDeleted}
          onClick={(event) => {
            event.stopPropagation();
            setProfile({ userId: user._id, mode: 'view' });
          }}
        >
          {row.displayName}
        </button>
        {row.email ? (
          <p className="truncate text-xs text-gray-500 dark:text-gray-400">{row.email}</p>
        ) : null}
      </div>
    </div>
  );

  /** First company leads with its colour dot; the rest collapse into a count. */
  const renderCompanies = (row: UserRow): React.ReactElement => {
    if (row.companies.length === 0) {
      return <span className="text-gray-400 dark:text-gray-500">No company</span>;
    }
    const [first, ...rest] = row.companies;
    return (
      <span className="flex min-w-0 items-center gap-2">
        <span
          aria-hidden
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: getCompanyColor(first.id).dot }}
        />
        <span className="truncate text-gray-700 dark:text-gray-300" title={first.name}>
          {first.name}
        </span>
        {rest.length > 0 ? (
          <span
            className="shrink-0 rounded-full bg-gray-100 px-1.5 text-[11px] font-medium tabular-nums text-gray-600 dark:bg-gray-700 dark:text-gray-300"
            title={rest.map((company) => company.name).join(', ')}
          >
            +{rest.length}
          </span>
        ) : null}
      </span>
    );
  };

  const renderStatus = (user: UserListItem, row: UserRow, size?: 'sm'): React.ReactElement => (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <Badge variant={STATUS_VARIANT[user.status] ?? 'warning'} dot size={size} className="capitalize">
        {user.status}
      </Badge>
      {row.lifecycleStatus ? (
        <Badge
          variant={
            row.lifecycleStatus === 'terminated'
              ? 'danger'
              : row.lifecycleStatus === 'probation'
                ? 'warning'
                : 'neutral'
          }
          size="sm"
          className="capitalize"
        >
          {row.lifecycleStatus}
        </Badge>
      ) : null}
    </span>
  );

  const renderDocuments = (user: UserListItem, row: UserRow): React.ReactElement => (
    <button
      type="button"
      className={cn(
        'inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 tabular-nums text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 dark:text-gray-400',
        row.isDeleted
          ? 'cursor-not-allowed opacity-50'
          : 'cursor-pointer hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-gray-100'
      )}
      aria-label={`Manage documents for ${row.displayName} (${row.documentCount})`}
      disabled={row.isDeleted}
      onClick={(event) => {
        event.stopPropagation();
        setDocumentsUserId(user._id);
      }}
    >
      <FileText className="h-3.5 w-3.5" aria-hidden />
      {row.documentCount}
    </button>
  );

  /**
   * The everyday verb (edit) sits inline on hover; the rarer and destructive
   * account operations live in the overflow menu rather than as a wall of
   * look-alike icons on every row.
   */
  /**
   * `layout` keeps the table's and the phone cards' menus distinct: both lists are
   * mounted (one hidden by CSS), and a shared id would open both portaled menus,
   * where the hidden one's outside-click handler swallows clicks on the visible one.
   */
  const renderActions = (
    user: UserListItem,
    row: UserRow,
    layout: 'table' | 'card'
  ): React.ReactElement | null => {
    const menuId = `${layout}:${user._id}`;
    if (!showActionsColumn) return null;
    if (row.isDeleted) {
      return canDeleteUser ? (
        <TableRowActions>
          <TableRowAction
            icon={RotateCcw}
            label={`Recover ${row.displayName}`}
            onSelect={() => handleRecoverClick({ _id: user._id, name: row.displayName })}
            disabled={recoverUser.isPending || !!userToRecover}
          />
        </TableRowActions>
      ) : null;
    }
    const hasMenu = canManageRoles || canUpdateUser || canDeleteUser;
    return (
      <TableRowActions className={cn(openMenuRowId === menuId && 'opacity-100')}>
        {canUpdateUser && (
          <TableRowAction
            icon={Pencil}
            label={`Edit ${row.displayName}`}
            onSelect={() => setProfile({ userId: user._id, mode: 'edit' })}
          />
        )}
        {hasMenu && (
          <TableRowActionsMenu
            rowId={menuId}
            openRowId={openMenuRowId}
            onOpenRowIdChange={setOpenMenuRowId}
            triggerAriaLabel={`More actions for ${row.displayName}`}
            menuAriaLabel={`Actions for ${row.displayName}`}
          >
            {canManageRoles && (
              <TableRowActionsMenuItem
                icon={<Shield className="h-4 w-4" aria-hidden />}
                onSelect={() => setRoleUserId(user._id)}
              >
                Change role
              </TableRowActionsMenuItem>
            )}
            {canUpdateUser && (
              <TableRowActionsMenuItem
                icon={<UserCheck className="h-4 w-4" aria-hidden />}
                onSelect={() => setStatusUserId(user._id)}
              >
                Change status
              </TableRowActionsMenuItem>
            )}
            {canUpdateUser && row.lifecycleStatus === 'probation' && (
              <TableRowActionsMenuItem
                icon={<BadgeCheck className="h-4 w-4" aria-hidden />}
                disabled={confirmPermanent.isPending}
                onSelect={() =>
                  confirmPermanent.mutate({
                    id: user._id,
                    payload: { confirmationDate: new Date().toISOString().slice(0, 10) },
                  })
                }
              >
                Confirm permanent
              </TableRowActionsMenuItem>
            )}
            {canUpdateUser && (
              <TableRowActionsMenuItem
                icon={<KeyRound className="h-4 w-4" aria-hidden />}
                onSelect={() =>
                  setUserToResetPassword({
                    _id: user._id,
                    email: user.email,
                    firstName: user.firstName,
                    lastName: user.lastName,
                  })
                }
              >
                Send password reset
              </TableRowActionsMenuItem>
            )}
            {canUpdateUser && user._id !== currentUserId && (
              <TableRowActionsMenuItem
                icon={<LogOut className="h-4 w-4" aria-hidden />}
                onSelect={() =>
                  setUserToForceLogout({
                    _id: user._id,
                    email: user.email,
                    firstName: user.firstName,
                    lastName: user.lastName,
                  })
                }
              >
                Sign out of all devices
              </TableRowActionsMenuItem>
            )}
            {canDeleteUser && (
              <>
                <TableRowActionsMenuDivider />
                <TableRowActionsMenuItem
                  icon={<UserX className="h-4 w-4" aria-hidden />}
                  variant="destructive"
                  onSelect={() => setUserToTerminate(user)}
                >
                  Terminate employee
                </TableRowActionsMenuItem>
                <TableRowActionsMenuItem
                  icon={<Trash2 className="h-4 w-4" aria-hidden />}
                  variant="destructive"
                  disabled={deleteUser.isPending || !!userToDelete}
                  onSelect={() => handleDeleteClick({ _id: user._id, name: row.displayName })}
                >
                  Delete user
                </TableRowActionsMenuItem>
              </>
            )}
          </TableRowActionsMenu>
        )}
      </TableRowActions>
    );
  };

  return (
    <>
      <DataTable className="hidden md:block" tableClassName="w-full" caption="Users">
        <DataTableHead>
          <tr>
            <DataTableHeadCell className="w-[30%] px-6 py-3">User</DataTableHeadCell>
            <DataTableHeadCell className="w-[20%] px-6 py-3">Companies</DataTableHeadCell>
            <DataTableHeadCell className="w-[11%] px-6 py-3">Employee ID</DataTableHeadCell>
            <DataTableHeadCell className="w-[11%] px-6 py-3">Role</DataTableHeadCell>
            <DataTableHeadCell className="w-[14%] px-6 py-3">Status</DataTableHeadCell>
            {canManageDocuments && (
              <DataTableHeadCell className="w-[7%] px-6 py-3">Docs</DataTableHeadCell>
            )}
            {showActionsColumn && (
              <DataTableHeadCell align="right" className="w-[7%] px-6 py-3">
                <span className="sr-only">Actions</span>
              </DataTableHeadCell>
            )}
          </tr>
        </DataTableHead>
        <DataTableBody>
          {users.map((user) => {
            const row = describeUser(user);
            return (
              <DataTableRow
                key={user._id}
                className={cn(
                  'group/row',
                  row.isDeleted ? 'text-gray-500 opacity-70' : 'cursor-pointer'
                )}
                onClick={() => handleRowActivate(user, row)}
              >
                <DataTableCell noWrap={false} className="max-w-0 px-6 py-3 align-middle">
                  {renderPerson(user, row, 'md')}
                </DataTableCell>
                <DataTableCell noWrap={false} className="max-w-0 px-6 py-3 align-middle">
                  {renderCompanies(row)}
                </DataTableCell>
                <DataTableCell className="px-6 py-3 align-middle tabular-nums text-gray-600 dark:text-gray-400">
                  {user.employeeId}
                </DataTableCell>
                <DataTableCell className="px-6 py-3 align-middle text-gray-700 dark:text-gray-300">
                  {row.roleName}
                </DataTableCell>
                <DataTableCell className="px-6 py-3 align-middle">
                  {renderStatus(user, row)}
                </DataTableCell>
                {canManageDocuments && (
                  <DataTableCell className="px-6 py-3 align-middle">
                    {renderDocuments(user, row)}
                  </DataTableCell>
                )}
                {showActionsColumn && (
                  <DataTableCell
                    align="right"
                    className="px-6 py-3 align-middle"
                    // Menu items render in a portal but still bubble through React;
                    // keep them from also opening the row.
                    onClick={(event) => event.stopPropagation()}
                  >
                    {renderActions(user, row, 'table')}
                  </DataTableCell>
                )}
              </DataTableRow>
            );
          })}
        </DataTableBody>
      </DataTable>

      {/* Phone layout: each user becomes a card with the same facts stacked. */}
      <ul className="flex flex-col gap-2 md:hidden">
        {users.map((user) => {
          const row = describeUser(user);
          return (
            <li
              key={user._id}
              className={cn(
                'group/row rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-colors dark:border-gray-700 dark:bg-gray-800',
                row.isDeleted
                  ? 'opacity-70'
                  : 'cursor-pointer active:bg-gray-50 dark:active:bg-gray-700/40'
              )}
              onClick={() => handleRowActivate(user, row)}
            >
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  {renderPerson(user, row, 'lg')}
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                    {renderCompanies(row)}
                    <span className="text-gray-500 dark:text-gray-400">{row.roleName}</span>
                    <span className="tabular-nums text-gray-500 dark:text-gray-400">
                      {user.employeeId}
                    </span>
                  </div>
                </div>
                <div
                  className="flex shrink-0 flex-col items-end gap-2"
                  onClick={(event) => event.stopPropagation()}
                >
                  {renderStatus(user, row, 'sm')}
                  <div className="flex items-center gap-1">
                    {canManageDocuments ? renderDocuments(user, row) : null}
                    {renderActions(user, row, 'card')}
                  </div>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {profile ? (
        <UserProfilePanel
          key={profile.userId}
          userId={profile.userId}
          initialMode={profile.mode}
          companies={(() => {
            const item = users.find((u) => u._id === profile.userId);
            return item?.companies?.length ? item.companies : item?.company ? [item.company] : [];
          })()}
          onClose={() => setProfile(null)}
          suspendDismiss={Boolean(
            statusUserId ||
              roleUserId ||
              userToTerminate ||
              userToResetPassword ||
              userToDelete ||
              documentsUserId
          )}
          onRoleRequested={setRoleUserId}
          onStatusRequested={setStatusUserId}
          onResetPasswordRequested={(user) => setUserToResetPassword(user)}
          onConfirmPermanentRequested={(user) =>
            confirmPermanent.mutate({
              id: user._id,
              payload: { confirmationDate: new Date().toISOString().slice(0, 10) },
            })
          }
          onTerminateRequested={(user) => {
            const listItem = users.find((item) => item._id === user._id);
            if (listItem) setUserToTerminate(listItem);
          }}
          onDeleteRequested={(user) =>
            handleDeleteClick({
              _id: user._id,
              name: `${user.firstName} ${user.lastName}`.trim() || user.email,
            })
          }
        />
      ) : null}

      <TerminateUserModal
        user={userToTerminate}
        onClose={() => setUserToTerminate(null)}
      />

      <ChangeUserStatusModal
        userId={statusUserId}
        onClose={() => setStatusUserId(null)}
        onSuccess={() => setStatusUserId(null)}
      />

      <ChangeUserRoleModal
        userId={roleUserId}
        onClose={() => setRoleUserId(null)}
        onSuccess={() => setRoleUserId(null)}
      />

      {documentsUserId &&
        (() => {
          const user = users.find((u) => u._id === documentsUserId);
          const userName = user?.name ?? user?.email ?? 'User';
          return (
            <Modal
              open={!!documentsUserId}
              onClose={() => setDocumentsUserId(null)}
              title={`Documents – ${userName}`}
              className="max-w-2xl"
              footer={
                <Button type="button" variant="secondary" onClick={() => setDocumentsUserId(null)}>
                  Close
                </Button>
              }
            >
              <UserDocumentsSection
                userId={documentsUserId}
                canUpload={can('documents', 'create')}
                canDelete={can('documents', 'delete')}
              />
            </Modal>
          );
        })()}

      <ConfirmDialog
        open={userToResetPassword !== null}
        onClose={() => setUserToResetPassword(null)}
        onConfirm={handleConfirmResetPassword}
        title="Send password reset"
        message={
          userToResetPassword
            ? adminPasswordResetConfirmMessage(
                `${userToResetPassword.firstName ?? ''} ${userToResetPassword.lastName ?? ''}`.trim() ||
                  userToResetPassword.email
              )
            : ''
        }
        confirmLabel="Send reset"
        confirmLoading={triggerPasswordReset.isPending}
      />

      <ConfirmDialog
        open={userToForceLogout !== null}
        onClose={() => setUserToForceLogout(null)}
        onConfirm={() => {
          if (!userToForceLogout) return;
          forceLogout.mutate(userToForceLogout._id, {
            onSuccess: () => setUserToForceLogout(null),
          });
        }}
        title="Sign out of all devices"
        message={
          userToForceLogout
            ? `Sign "${`${userToForceLogout.firstName ?? ''} ${userToForceLogout.lastName ?? ''}`.trim() || userToForceLogout.email}" out everywhere? They can sign back in with their password. To lock them out, change their account status instead.`
            : ''
        }
        confirmLabel="Sign out"
        confirmLoading={forceLogout.isPending}
      />

      <ConfirmDialog
        open={userToDelete !== null}
        onClose={() => setUserToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Soft-delete user"
        message={
          userToDelete
            ? `Soft-delete user "${userToDelete.name}"? They will no longer appear in the list.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        confirmLoading={deleteUser.isPending}
      />

      <ConfirmDialog
        open={userToRecover !== null}
        onClose={() => setUserToRecover(null)}
        onConfirm={handleConfirmRecover}
        title="Recover user"
        message={
          userToRecover
            ? `Recover user "${userToRecover.name}"? They will appear in the user list again.`
            : ''
        }
        confirmLabel="Recover"
        confirmLoading={recoverUser.isPending}
      />
    </>
  );
}

UserList.displayName = 'UserList';
